import { Injectable, OnDestroy, inject } from '@angular/core';
import { WebSocketLink } from '@apollo/client/link/ws';
import { Subscription } from 'rxjs';
import { AuthInitializerService } from '../../shared/auth/auth-initializer.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private wsLink: WebSocketLink;
  private readonly maxReconnectAttempts = 5;
  private readonly subscription$ = new Subscription();
  private readonly authInitializer = inject(AuthInitializerService);
  private connectionToken: string | null = null;

  constructor(private configService: ConfigService) {
    this.initializeWebSocket();
    this.watchForTokenChanges();
  }

  private initializeWebSocket(): void {
    this.wsLink = new WebSocketLink({
      uri: this.configService.api.gqlWsUrl,
      options: {
        // AuthWaitLink runs before this link. Lazy mode prevents the physical
        // socket from opening at app bootstrap, before Auth0 has supplied a
        // token (#14141).
        lazy: true,
        reconnect: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        connectionParams: async () => {
          const params = await this.getConnectionParams();
          return { ...params, subprotocol: 'graphql-ws' };
        },
        connectionCallback: (error) => this.handleConnectionCallback(error),
        timeout: 10000,
        inactivityTimeout: 30000,
      },
    });

    this.setupEventListeners();
  }

  private async getConnectionParams(): Promise<{ headers: { Authorization: string } }> {
    await this.authInitializer.waitForAuth();
    const token = await this.authInitializer.getToken();

    if (!token) {
      throw new Error('Cannot open GraphQL WebSocket without an access token');
    }

    this.connectionToken = token;
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  }

  private handleConnectionCallback(error: any): void {
    if (error) {
      console.error('WebSocket connection error:', error);
    } else {
      console.log('WebSocket connected successfully');
    }
  }

  private setupEventListeners(): void {
    const wsClient = (this.wsLink as any).subscriptionClient;
    if (!wsClient) return;

    wsClient.onConnecting = () => console.log('WebSocket: Connecting...');
    wsClient.onConnected = () => console.log('WebSocket: Connected');
    wsClient.onReconnecting = () => console.log('WebSocket: Reconnecting...');
    wsClient.onReconnected = () => console.log('WebSocket: Reconnected');
    wsClient.onDisconnected = () => console.log('WebSocket: Disconnected');
    wsClient.onError = (error: any) => console.error('WebSocket error:', error);
  }

  private watchForTokenChanges(): void {
    const tokenSubscription = this.authInitializer.token$.subscribe(token => {
      if (token && this.connectionToken && token !== this.connectionToken) {
        this.connectionToken = token;
        this.reconnect();
      }
    });
    this.subscription$.add(tokenSubscription);
  }

  /**
   * Force a reconnection with a fresh token
   * This should be called when the authentication state changes (e.g., instance switch)
   */
  public async reconnectWithFreshToken(): Promise<void> {
    console.log('Forcing WebSocket reconnection with fresh token...');
    const previousConnectionToken = this.connectionToken;
    const token = await this.authInitializer.forceTokenRefresh();
    // A changed token is handled by watchForTokenChanges(). If Auth0 returns
    // the same token, reconnect explicitly to preserve this method's contract.
    if (token && token === previousConnectionToken) {
      this.connectionToken = token;
      this.reconnect();
    }
  }

  private reconnect(): void {
    const wsClient = (this.wsLink as any).subscriptionClient;
    // A non-forced close preserves active operations and asks
    // subscriptions-transport-ws to reconnect. connectionParams is then
    // evaluated again with the refreshed token.
    wsClient?.close(false, false);
  }

  private cleanup(): void {
    const wsClient = this.wsLink && (this.wsLink as any).subscriptionClient;
    if (wsClient) {
      try {
        wsClient.close(true, true);
        // Clean up event listeners
        wsClient.onConnecting = null;
        wsClient.onConnected = null;
        wsClient.onReconnecting = null;
        wsClient.onReconnected = null;
        wsClient.onDisconnected = null;
        wsClient.onError = null;
      } catch (error) {
        console.error('Error cleaning up WebSocket:', error);
      }
    }
  }

  getWebSocketLink(): WebSocketLink {
    return this.wsLink;
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
    this.cleanup();
  }
}
